import {
  Auth,
  AuthCredential,
  AuthError,
  UserCredential,
  createUserWithEmailAndPassword,
  signInWithCredential,
  signInWithCustomToken,
  signInWithEmailAndPassword,
  signOut,
} from "firebase/auth";
import {
  UseMutationOptions,
  UseMutationResult,
  useMutation,
} from "@tanstack/react-query";

export function useAuthCreateUserWithEmailAndPassword(
  auth: Auth,
  useMutationOptions?: UseMutationOptions<
    UserCredential,
    AuthError,
    { email: string; password: string }
  >,
): UseMutationResult<
  UserCredential,
  AuthError,
  { email: string; password: string }
> {
  return useMutation<
    UserCredential,
    AuthError,
    { email: string; password: string }
  >({
    mutationFn: ({ email, password }) => {
      return createUserWithEmailAndPassword(auth, email, password);
    },
    ...useMutationOptions,
  });
}

export function useAuthSignInWithCredential(
  auth: Auth,
  useMutationOptions?: UseMutationOptions<
    UserCredential,
    AuthError,
    AuthCredential
  >,
): UseMutationResult<UserCredential, AuthError, AuthCredential> {
  return useMutation<UserCredential, AuthError, AuthCredential>({
    mutationFn: (credential) => {
      return signInWithCredential(auth, credential);
    },
    ...useMutationOptions,
  });
}

export function useAuthSignInWithEmailAndPassword(
  auth: Auth,
  useMutationOptions?: UseMutationOptions<
    UserCredential,
    AuthError,
    { email: string; password: string }
  >,
): UseMutationResult<
  UserCredential,
  AuthError,
  { email: string; password: string }
> {
  return useMutation<
    UserCredential,
    AuthError,
    { email: string; password: string }
  >({
    mutationFn: ({ email, password }) => {
      return signInWithEmailAndPassword(auth, email, password);
    },
    ...useMutationOptions,
  });
}

export function useAuthSignOut(
  auth: Auth,
  useMutationOptions?: UseMutationOptions<void, AuthError, void>,
): UseMutationResult<void, AuthError, void> {
  return useMutation<void, AuthError, void>({
    mutationFn: () => {
      return signOut(auth);
    },
    ...useMutationOptions,
  });
}

export function useAuthSignInWithCustomToken(
  auth: Auth,
  useMutationOptions?: UseMutationOptions<
    UserCredential,
    AuthError,
    { token: string }
  >,
): UseMutationResult<UserCredential, AuthError, { token: string }> {
  return useMutation<UserCredential, AuthError, { token: string }>({
    mutationFn: ({ token }) => {
      return signInWithCustomToken(auth, token);
    },
    ...useMutationOptions,
  });
}
